import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';

// Public pages
import SignInPage from '@/app/sign-in/[[...sign-in]]/page';
import SignUpPage from '@/app/sign-up/[[...sign-up]]/page';
import UnauthorizedPage from '@/app/unauthorized/page';
import PrivacyPolicyPage from '@/app/privacy/page';

// Protected pages
import DashboardPage from '@/app/dashboard/page';
import BootstrapPage from '@/app/bootstrap/page';
import TodosPage from '@/app/todos/page';

// Coach pages
import CoachLayout from '@/app/coach/layout';
import CoachIndexPage from '@/app/coach/page';
import CoachPlayersPage from '@/app/coach/players/page';
import CoachPlayerDetailPage from '@/app/coach/players/[playerId]/page';
import CoachSessionsPage from '@/app/coach/sessions/page';
import CoachAttendancePage from '@/app/coach/sessions/[sessionId]/attendance/page';
import CoachMessagesPage from '@/app/coach/messages/page';
import CoachAnnouncementsPage from '@/app/coach/announcements/page';
import CoachCalendarPage from '@/app/coach/calendar/page';
import CoachMakeupsPage from '@/app/coach/makeups/page';
import CoachLeaderboardsPage from '@/app/coach/leaderboards/page';

// Director pages
import DirectorLayout from '@/app/director/layout';
import DirectorIndexPage from '@/app/director/page';
import DirectorPlayersPage from '@/app/director/players/page';
import DirectorPlayerDetailPage from '@/app/director/players/[playerId]/page';
import DirectorCoachesPage from '@/app/director/coaches/page';
import DirectorParentsPage from '@/app/director/parents/page';
import DirectorSessionsPage from '@/app/director/sessions/page';
import DirectorSessionDetailPage from '@/app/director/sessions/[sessionId]/page';
import DirectorInvoicesPage from '@/app/director/invoices/page';
import DirectorAnalyticsPage from '@/app/director/analytics/page';
import DirectorAnnouncementsPage from '@/app/director/announcements/page';
import DirectorSettingsPage from '@/app/director/settings/page';
import DirectorFeedbackPage from '@/app/director/feedback/page';
import DirectorMakeupsPage from '@/app/director/makeups/page';
import DirectorCalendarPage from '@/app/director/calendar/page';
import DirectorAttendanceReportPage from '@/app/director/attendance-report/page';

// Parent pages
import ParentLayout from '@/app/parent/layout';
import OnboardLayout from '@/app/parent/onboard-layout';
import ParentIndexPage from '@/app/parent/page';
import ParentChildrenPage from '@/app/parent/children/page';
import ParentChildDetailPage from '@/app/parent/children/[childId]/page';
import ParentMessagesPage from '@/app/parent/messages/page';
import ParentAnnouncementsPage from '@/app/parent/announcements/page';
import ParentCalendarPage from '@/app/parent/calendar/page';
import ParentInvoicesPage from '@/app/parent/invoices/page';
import ParentJoinPage from '@/app/parent/join/page';
import ParentOnboardPage from '@/app/parent/onboard/page';

// Kid Mode pages
import KidLayout from '@/app/kid/layout';
import KidHomePage from '@/app/kid/page';
import KidBadgesPage from '@/app/kid/badges/page';
import KidMissionsPage from '@/app/kid/missions/page';

// Protected route wrapper with loading spinner
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded } = useAuth();
  const location = useLocation();

  // Prevent flash of unauthenticated content
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const redirectUrl = encodeURIComponent(location.pathname + location.search);

  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <Navigate to={`/sign-in?redirect_url=${redirectUrl}`} replace />
      </SignedOut>
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Navigate to="/sign-in" replace />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bootstrap"
        element={
          <ProtectedRoute>
            <BootstrapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/todos"
        element={
          <ProtectedRoute>
            <TodosPage />
          </ProtectedRoute>
        }
      />

      {/* Coach routes */}
      <Route
        path="/coach"
        element={
          <ProtectedRoute>
            <CoachLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CoachIndexPage />} />
        <Route path="players" element={<CoachPlayersPage />} />
        <Route path="players/:playerId" element={<CoachPlayerDetailPage />} />
        <Route path="sessions" element={<CoachSessionsPage />} />
        <Route path="sessions/:sessionId/attendance" element={<CoachAttendancePage />} />
        <Route path="calendar" element={<CoachCalendarPage />} />
        <Route path="makeups" element={<CoachMakeupsPage />} />
        <Route path="messages" element={<CoachMessagesPage />} />
        <Route path="announcements" element={<CoachAnnouncementsPage />} />
        <Route path="leaderboards" element={<CoachLeaderboardsPage />} />
      </Route>

      {/* Director routes */}
      <Route
        path="/director"
        element={
          <ProtectedRoute>
            <DirectorLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DirectorIndexPage />} />
        <Route path="players" element={<DirectorPlayersPage />} />
        <Route path="players/:playerId" element={<DirectorPlayerDetailPage />} />
        <Route path="coaches" element={<DirectorCoachesPage />} />
        <Route path="parents" element={<DirectorParentsPage />} />
        <Route path="sessions" element={<DirectorSessionsPage />} />
        <Route path="sessions/:sessionId" element={<DirectorSessionDetailPage />} />
        <Route path="calendar" element={<DirectorCalendarPage />} />
        <Route path="invoices" element={<DirectorInvoicesPage />} />
        <Route path="makeups" element={<DirectorMakeupsPage />} />
        <Route path="attendance-report" element={<DirectorAttendanceReportPage />} />
        <Route path="analytics" element={<DirectorAnalyticsPage />} />
        <Route path="announcements" element={<DirectorAnnouncementsPage />} />
        <Route path="settings" element={<DirectorSettingsPage />} />
        <Route path="feedback" element={<DirectorFeedbackPage />} />
      </Route>

      {/* Parent routes */}
      <Route
        path="/parent"
        element={
          <ProtectedRoute>
            <ParentLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ParentIndexPage />} />
        <Route path="children" element={<ParentChildrenPage />} />
        <Route path="children/:childId" element={<ParentChildDetailPage />} />
        <Route path="calendar" element={<ParentCalendarPage />} />
        <Route path="messages" element={<ParentMessagesPage />} />
        <Route path="announcements" element={<ParentAnnouncementsPage />} />
        <Route path="invoices" element={<ParentInvoicesPage />} />
      </Route>

      {/* Focused invite-code flows — no sidebar so parent can't navigate away mid-flow */}
      <Route
        element={
          <ProtectedRoute>
            <OnboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/parent/onboard" element={<ParentOnboardPage />} />
        <Route path="/parent/join" element={<ParentJoinPage />} />
      </Route>

      {/* Kid Mode routes */}
      <Route
        path="/kid/:childId"
        element={
          <ProtectedRoute>
            <KidLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<KidHomePage />} />
        <Route path="badges" element={<KidBadgesPage />} />
        <Route path="missions" element={<KidMissionsPage />} />
      </Route>
    </Routes>
    </>
  );
}
